import { Box, LinearProgress, makeStyles, Typography } from "@material-ui/core";
import { alpha } from "@material-ui/core/styles";
import * as React from "react";
import { useHistory, useLocation } from "react-router";
import { useRecoilStateLoadable } from "recoil";

import { IdName, User } from "@/api/generated";
import { Loading } from "@/components/Loading";
import { layoutTokens, textStyles } from "@/designTokens";
import { useDevice } from "@/hooks/useDevice";
import { useGetMasterResidenceTypes } from "@/hooks/useGetMasterData";
import { BaseTemplate } from "@/layouts/BaseTemplate";
import theme from "@/theme";
import { getAppVersionTag, initializeAppInfo } from "@/utilities/appInfo";
import { UserState } from "@/view_models/atoms";

import chevron from "../../../assets/image/settings_v2/chevron-forward.svg";

import { BasicInfo } from "./components/items/BasicInfo";
import { DeleteAccount } from "./components/items/DeleteAccount";
import { LogoutTab } from "./components/items/Logout";
import { ReportTab } from "./components/items/Report";
import { ResetPassword } from "./components/items/ResetPassword";
import { UpdateEmail } from "./components/items/UpdateEmail";
import { SettingsCard } from "./components/SettingsCard";
import { calculateProfileCompletion, getProfileCompletionFromUser } from "./utils/profileCompletion";

const useStyles = makeStyles({
  frame: { padding: "24px 24px 32px", [theme.breakpoints.up("md")]: { padding: "24px 0 40px" } },
  columns: {
    display: "flex",
    flexDirection: "column",
    gap: layoutTokens.space[32],
    [theme.breakpoints.up("md")]: { flexDirection: "row", alignItems: "flex-start", gap: layoutTokens.space[40] },
  },
  menu: {
    display: "flex",
    flexDirection: "column",
    gap: layoutTokens.space[32],
    width: "100%",
    [theme.breakpoints.up("md")]: { flex: "0 0 32%", gap: layoutTokens.space[16] },
  },
  group: { display: "flex", flexDirection: "column", gap: layoutTokens.space[16] },
  rows: { display: "flex", flexDirection: "column", gap: layoutTokens.space[8] },
  heading: { ...textStyles["SP/Heading/H4"], [theme.breakpoints.up("md")]: textStyles["PC/Heading/H4"] },
  caption: { ...textStyles["SP/Body/Small"], color: theme.palette.text.secondary },
  rate: {
    backgroundColor: theme.palette.semantic["surface/card"],
    borderRadius: layoutTokens.radius.lg,
    padding: layoutTokens.space[16],
    display: "flex",
    flexDirection: "column",
    gap: layoutTokens.space[16],
    boxShadow: `0 2px 8px ${alpha(theme.palette.text.primary, 0.06)}`,
  },
  name: { ...textStyles["SP/Heading/H3"], [theme.breakpoints.up("md")]: textStyles["PC/Heading/H3"] },
  profileCaption: {
    ...textStyles["SP/Body/Small"],
    color: theme.palette.text.secondary,
    [theme.breakpoints.up("md")]: textStyles["PC/Body/Small"],
  },
  percentage: { ...textStyles["Number/S"] },
  pageTitle: { ...textStyles["PC/Heading/H2"], marginBottom: layoutTokens.space[24] },
  progress: { borderRadius: layoutTokens.radius.full, height: 6 },
  content: {
    minWidth: 0,
    width: "100%",
    [theme.breakpoints.up("md")]: {
      flex: 1,
      padding: "32px 32px 40px",
      backgroundColor: theme.palette.semantic["surface/card"],
      borderRadius: layoutTokens.radius.lg,
    },
  },
  hubContent: { [theme.breakpoints.up("md")]: { padding: 0, background: "none" } },
  inner: { display: "flex", flexDirection: "column", [theme.breakpoints.up("md")]: { maxWidth: 560 } },
  formInner: { [theme.breakpoints.down("sm")]: { minHeight: "calc(100dvh - 112px)" } },
  back: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: 0,
    marginBottom: 16,
    border: 0,
    background: "none",
    color: theme.palette.primary.main,
    ...textStyles["PC/Button/M"],
    cursor: "pointer",
  },
  backChevron: { width: 6, height: 10, transform: "rotate(180deg)" },
  title: { ...textStyles["PC/Heading/H3"], marginBottom: layoutTokens.space[24] },
  logout: {
    border: 0,
    borderRadius: layoutTokens.radius.lg,
    padding: layoutTokens.space[16],
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.semantic["surface/card"],
    ...textStyles["SP/Button/M"],
    boxShadow: `0 2px 8px ${alpha(theme.palette.text.primary, 0.06)}`,
    cursor: "pointer",
  },
  withdrawal: {
    color: theme.palette.text.secondary,
    background: "none",
    border: 0,
    ...textStyles["SP/Button/M"],
    cursor: "pointer",
    padding: layoutTokens.space[16],
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: layoutTokens.space[16],
  },
});

export interface SettingItemsProps {
  user?: User;
  setUser?: (user: User) => void;
  masterResidenceTypes?: IdName[];
  onBack?: () => void;
  onHeaderChange?: (title: string, onBack: () => void) => void;
  onDraftChange?: (values: Record<string, unknown> | null) => void;
}
const items: Record<string, { label: string; title?: string; component: React.FC<SettingItemsProps> }> = {
  basic: { label: "基本情報", component: BasicInfo },
  email: { label: "メールアドレス", title: "メールアドレスの変更", component: UpdateEmail },
  password: { label: "パスワード", title: "パスワードの変更", component: ResetPassword },
  report: { label: "不具合・ご要望", component: ReportTab },
  delete: { label: "退会", component: DeleteAccount },
};

export const SettingsPage: React.FC = () => {
  const { isMobile } = useDevice();
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation<{ initialSetting?: string }>();
  const [currentSetting, setCurrentSetting] = React.useState("menu");
  const [settingRevision, setSettingRevision] = React.useState(0);
  const [childHeader, setChildHeader] = React.useState<{ title: string; onBack: () => void } | null>(null);
  const [draft, setDraft] = React.useState<Record<string, unknown> | null>(null);
  const onDraftChange = React.useCallback((values: Record<string, unknown> | null) => setDraft(values), []);
  const [logoutOpen, setLogoutOpen] = React.useState(false);
  const [version, setVersion] = React.useState<string | null>(getAppVersionTag());
  const [loadable, setUser] = useRecoilStateLoadable(UserState);
  const { masterResidenceTypes, loading } = useGetMasterResidenceTypes();
  const previousLocationKey = React.useRef<string>();
  const selectSetting = React.useCallback((setting: string) => {
    setChildHeader(null);
    setSettingRevision((previous) => previous + 1);
    if (setting === "logout") setLogoutOpen(true);
    else setCurrentSetting(setting);
  }, []);
  const onBack = React.useCallback(() => selectSetting("menu"), [selectSetting]);
  const onHeaderChange = React.useCallback(
    (title: string, back: () => void) => setChildHeader({ title, onBack: back }),
    []
  );
  React.useEffect(() => {
    const key = location.key ?? "__default__";
    if (!location.state?.initialSetting || previousLocationKey.current === key) return;
    previousLocationKey.current = key;
    selectSetting(location.state.initialSetting);
  }, [location.state?.initialSetting, location.key, selectSetting]);
  React.useEffect(() => {
    let mounted = true;
    Promise.resolve(initializeAppInfo()).then(() => {
      if (mounted) setVersion(getAppVersionTag());
    });
    return () => {
      mounted = false;
    };
  }, []);
  if (loadable.state === "hasError") throw loadable.contents;
  if (loadable.state === "loading" || !loadable.contents || loading)
    return (
      <BaseTemplate headerTitle="設定" isDense>
        <Loading />
      </BaseTemplate>
    );
  const user = loadable.contents;
  const completion = draft ? calculateProfileCompletion(draft) : getProfileCompletionFromUser(user);
  const active = currentSetting === "menu" && !isMobile ? "basic" : currentSetting;
  const selected = items[active];
  const Component = selected?.component;
  const title = childHeader?.title || selected?.title || selected?.label || "設定";
  const isProfileHub = active === "basic" && title === "基本情報";
  const menu = (
    <Box component="nav" aria-label="設定メニュー" className={classes.menu}>
      <Box className={classes.rate}>
        <Box display="flex" flexDirection="column" style={{ gap: 4 }}>
          <Typography className={classes.name}>
            {user.family_name} {user.first_name}
          </Typography>
          <Typography className={classes.profileCaption}>ID {user.identifier}</Typography>
        </Box>
        <Box display="flex" flexDirection="column" style={{ gap: 8 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography className={classes.profileCaption}>プロフィール入力率</Typography>
            <Typography className={classes.percentage}>{completion.rate}%</Typography>
          </Box>
          <LinearProgress className={classes.progress} variant="determinate" value={completion.rate} />
        </Box>
        <Typography className={classes.profileCaption}>入力率が高いほど、届く提案が最適化されます。</Typography>
      </Box>
      <Box className={classes.group}>
        <Typography className={classes.heading}>アカウント</Typography>
        <Box className={classes.rows}>
          {["basic", "email", "password"].map((key) => (
            <SettingsCard
              key={key}
              label={items[key].label}
              selected={!isMobile && active === key}
              onClick={() => selectSetting(key)}
            />
          ))}
        </Box>
      </Box>
      <Box className={classes.group}>
        <Typography className={classes.heading}>サポート</Typography>
        <SettingsCard
          label={items.report.label}
          selected={!isMobile && active === "report"}
          onClick={() => selectSetting("report")}
        />
      </Box>
      <Box className={classes.group}>
        <Typography className={classes.heading}>このアプリについて</Typography>
        <Box className={classes.rows}>
          <SettingsCard
            label="利用規約"
            onClick={() => window.open("https://lp.propally.jp/termsofuse.html", "_blank", "noopener,noreferrer")}
          />
          <SettingsCard
            label="プライバシーポリシー"
            onClick={() => window.open("https://propally.co.jp/privacypolicy.html", "_blank", "noopener,noreferrer")}
          />
        </Box>
      </Box>
      <button type="button" className={classes.logout} onClick={() => setLogoutOpen(true)}>
        ログアウト
      </button>
      <button type="button" className={classes.withdrawal} onClick={() => selectSetting("delete")}>
        退会する
        <img src={chevron} width={6} height={10} alt="" />
      </button>
      <Typography align="center" className={classes.caption}>
        バージョン {version || "取得できません"}
      </Typography>
    </Box>
  );
  return (
    <BaseTemplate
      headerTitle={isMobile ? (currentSetting === "menu" ? "設定" : title) : undefined}
      isDense
      isPcV2
      isBackGroundWhite={isMobile && currentSetting !== "menu" && !(active === "basic" && title === "基本情報")}
      disableBottomPadding
      onBack={
        isMobile ? (currentSetting === "menu" ? () => history.goBack() : childHeader?.onBack || onBack) : undefined
      }
    >
      <Box className={classes.frame}>
        {!isMobile && (
          <Typography component="h1" className={classes.pageTitle}>
            設定
          </Typography>
        )}
        <Box className={classes.columns}>
          {(currentSetting === "menu" || !isMobile) && menu}
          {Component && (
            <Box className={`${classes.content} ${isProfileHub ? classes.hubContent : ""}`}>
              {!isMobile && active === "basic" && !isProfileHub && childHeader && (
                <button
                  className={classes.back}
                  type="button"
                  aria-label={title === "電話番号の確認" ? "プロフィールに戻る" : "基本情報に戻る"}
                  onClick={childHeader.onBack}
                >
                  <img src={chevron} className={classes.backChevron} alt="" />
                  {title === "電話番号の確認" ? "プロフィール" : "基本情報"}
                </button>
              )}
              {!isMobile && (
                <Typography component="h2" className={classes.title}>
                  {title}
                </Typography>
              )}
              <Box className={`${classes.inner} ${!isProfileHub ? classes.formInner : ""}`}>
                <Component
                  key={`${active}-${settingRevision}`}
                  user={user}
                  setUser={(response) =>
                    setUser((current) => (current?.id === response.id ? { ...current, ...response } : current))
                  }
                  masterResidenceTypes={masterResidenceTypes}
                  onBack={onBack}
                  onHeaderChange={onHeaderChange}
                  onDraftChange={onDraftChange}
                />
              </Box>
            </Box>
          )}
        </Box>
      </Box>
      {logoutOpen && <LogoutTab onBack={() => setLogoutOpen(false)} />}
    </BaseTemplate>
  );
};
